'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import type { RouteEntry } from '@/config/routes';

type NavGroup = { label: string; items: readonly RouteEntry[] };

/** Accessible click, hover and keyboard navigation for pointer and non-pointer users. */
export function DesktopNavigation({ groups, descriptions }: { groups: readonly NavGroup[]; descriptions: Readonly<Record<string, string>> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  const closeSoon = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpenIndex(null), 140); };

  useEffect(() => {
    const outside = (event: PointerEvent) => { if (!navRef.current?.contains(event.target as Node)) setOpenIndex(null); };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || openIndex === null) return;
      event.preventDefault();
      const index = openIndex;
      setOpenIndex(null);
      navRef.current?.querySelector<HTMLButtonElement>(`[data-menu-trigger="${index}"]`)?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', keyboard);
    return () => { cancelClose(); document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', keyboard); };
  }, [openIndex]);

  return <nav ref={navRef} className="desktop-navigation" aria-label="ناوبری اصلی">
    {groups.map((group, index) => {
      const menuId = `desktop-menu-${index}`;
      const isOpen = openIndex === index;
      return <div className="navigation-menu" data-open={isOpen || undefined} key={group.label}
        onPointerEnter={() => { cancelClose(); setOpenIndex(index); }} onPointerLeave={closeSoon}
        onFocus={() => { cancelClose(); setOpenIndex(index); }} onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpenIndex(null);
        }}>
        <button type="button" data-menu-trigger={index} aria-expanded={isOpen} aria-controls={menuId}
          onClick={() => setOpenIndex(isOpen ? null : index)} onKeyDown={event => {
            if (event.key !== 'ArrowDown') return;
            event.preventDefault(); setOpenIndex(index);
            requestAnimationFrame(() => document.querySelector<HTMLAnchorElement>(`#${menuId} a`)?.focus());
          }}>
          {group.label}<span aria-hidden className="navigation-menu__chevron">⌄</span>
        </button>
        <div className="navigation-menu__panel" id={menuId} aria-hidden={!isOpen}>
          {group.items.map(item => <Link key={item.id} href={item.href} tabIndex={isOpen ? 0 : -1}>
            <strong>{item.label}</strong><span>{descriptions[item.id] ?? 'راهکار تخصصی TripSoft'}</span>
          </Link>)}
        </div>
      </div>;
    })}
    <Link href="/blog/">وبلاگ</Link><Link href="/about/">درباره ما</Link>
  </nav>;
}
